import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AudioWaveform, RotateCw } from 'lucide-react';
import CreatorEditor, { activeExport } from '../components/creator/CreatorEditor';
import { clearCreatorSession, creatorApi, creatorError, readCreatorSession, storeCreatorSession } from '../lib/creatorApi';
import { assertCreatorProjectCompatible, normalizeCreatorProject } from '../lib/creatorProject';
import { addAssetClip, createHistory, pushHistory, redoHistory, undoHistory } from '../lib/creatorTimeline';
import { checkCreatorUpload, creatorUploadError } from '../lib/creatorUpload';
import '../components/creator/creator.css';

export default function CreatorStudioPage() {
  const [project, setProject] = useState(null);
  const [history, setHistory] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recoveryExpired, setRecoveryExpired] = useState(false);
  const [storageWarning, setStorageWarning] = useState(false);
  const [saveState, setSaveState] = useState('saved');
  const [editVersion, setEditVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [failedUpload, setFailedUpload] = useState(null);
  const uploadController = useRef(null);
  const [exportJob, setExportJob] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const data = useRef({ project: null, history: null, session: null, version: 0, savedVersion: 0 });
  const queue = useRef(Promise.resolve());
  const alive = useRef(false);
  const initialization = useRef(null);
  const lastActivity = useRef(0);
  const activity = useCallback(() => {
    if (!data.current.session || Date.now() - lastActivity.current < 60000) return;
    lastActivity.current = Date.now();
    creatorApi.activity(data.current.session).catch(() => {});
  }, []);
  useEffect(() => {
    if (!playing) return undefined;
    activity();
    const timer = setInterval(activity, 60000);
    return () => clearInterval(timer);
  }, [playing, activity]);
  const enqueue = useCallback(action => {
    const result = queue.current.then(action);
    queue.current = result.catch(() => {});
    return result;
  }, []);

  const initialize = useCallback(async fresh => {
    setLoading(true); setError(''); setRecoveryExpired(false);
    try {
      if (fresh) { clearCreatorSession(); initialization.current = null; }
      if (!initialization.current) initialization.current = (async () => {
        const stored = readCreatorSession();
        const result = stored ? await creatorApi.load(stored) : await creatorApi.create();
        const capability = stored || { project_id: result.project.project_id, access_token: result.access_token };
        const persisted = storeCreatorSession(capability);
        return { ...result, capability, persisted };
      })();
      const result = await initialization.current;
      if (!alive.current) return;
      assertCreatorProjectCompatible(result.project);
      const normalizedProject = normalizeCreatorProject(result.project);
      const initialHistory = createHistory(normalizedProject);
      data.current = { project: normalizedProject, history: initialHistory, session: result.capability, version: 0, savedVersion: 0 };
      setProject(normalizedProject); setHistory(initialHistory); setSession(result.capability);
      setStorageWarning(!result.persisted); setSaveState('saved'); setEditVersion(0); setExportJob(result.export || null);
    } catch (failure) {
      initialization.current = null;
      if (!alive.current) return;
      setRecoveryExpired([401, 403, 404, 410].includes(failure?.response?.status));
      setError(creatorError(failure, failure.message || 'No se pudo abrir el proyecto. Reintenta sin cerrar esta pestana.'));
    } finally { if (alive.current) setLoading(false); }
  }, []);

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    initialize(false);
    creatorApi.capabilities(controller.signal).then(result => { if (alive.current) setCapabilities(result); }).catch(failure => {
      if (alive.current && !controller.signal.aborted) setError(creatorError(failure, 'No se pudieron consultar los formatos de exportacion. Reintenta la consulta.'));
    });
    return () => { alive.current = false; controller.abort(); uploadController.current?.abort(); };
  }, [initialize]);

  const commitHistory = useCallback(nextHistory => {
    const current = data.current;
    if (!current.project || current.mediaBusy || nextHistory === current.history) return;
    current.history = nextHistory;
    current.project = { ...current.project, ...nextHistory.present };
    current.version += 1;
    setHistory(nextHistory); setProject(current.project); setEditVersion(current.version); setSaveState('dirty');
  }, []);
  const change = useCallback((operation, label) => {
    const current = data.current;
    if (!current.project) return;
    commitHistory(pushHistory(current.history, operation(current.project), label));
  }, [commitHistory]);
  const saveCurrent = useCallback(async () => {
    const current = data.current;
    if (!current.project || current.savedVersion === current.version) return current.project;
    const snapshot = current.project;
    const version = current.version;
    if (alive.current) setSaveState('saving');
    try {
      const result = await creatorApi.save(current.session, snapshot);
      current.project = { ...result.project, ...current.history.present };
      current.savedVersion = version;
      if (alive.current) { setProject(current.project); setSaveState(current.version === version ? 'saved' : 'dirty'); setError(''); }
      return current.project;
    } catch (failure) {
      if (failure?.response?.status === 409) {
        try {
          const latest = await creatorApi.load(current.session);
          current.project = { ...latest.project, ...current.history.present };
          if (alive.current) setProject(current.project);
        } catch { /* Keep the unsaved metadata available if recovery also fails. */ }
      }
      if (alive.current) {
        setSaveState('error');
        setError(creatorError(failure, failure?.response?.status === 409
          ? 'El servidor tiene otra revision. Tus cambios siguen abiertos; vuelve a intentar guardar antes de exportar.'
          : 'No se guardaron los cambios. Conserva esta pestana y vuelve a guardar.'));
      }
      throw failure;
    }
  }, []);
  const save = useCallback(() => enqueue(saveCurrent).catch(() => {}), [enqueue, saveCurrent]);
  useEffect(() => {
    if (!editVersion) return undefined;
    const timer = setTimeout(save, 800);
    return () => clearTimeout(timer);
  }, [editVersion, save]);
  useEffect(() => {
    const beforeUnload = event => {
      if (data.current.version === data.current.savedVersion) return;
      event.preventDefault(); event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  const upload = useCallback(async (file, options = {}) => {
    if (!file) return;
    setUploading(true); setUploadProgress(0); setUploadStage('uploading'); setFailedUpload(null); setError('');
    try {
      checkCreatorUpload(file, capabilities);
      return await enqueue(async () => {
        const controller = new AbortController(); uploadController.current = controller;
        await saveCurrent();
        const current = data.current;
        const result = await creatorApi.upload(current.session, file, event => {
          if (alive.current && event.total) setUploadProgress(Math.round(event.loaded / event.total * 100));
        }, { signal: controller.signal, capabilities, onStage: stage => { if (alive.current) setUploadStage(stage); } });
        current.project = { ...result.project, ...current.history.present };
        if (alive.current) setProject(current.project);
        if (options.addToTimeline !== false) change(value => addAssetClip(value, result.asset, 0), 'Importar medio');
        return result.asset;
      });
    } catch (failure) {
      if (alive.current) { setError(creatorUploadError(failure)); setFailedUpload(failure?.response?.status === 413 ? null : { file, options }); }
      throw failure;
    } finally { uploadController.current = null; if (alive.current) { setUploading(false); setUploadStage(''); } }
  }, [enqueue, saveCurrent, change, capabilities]);

  const mediaAction = useCallback(async (action, asset, payload = {}) => {
    if (data.current.mediaBusy) throw new Error('Espera a que termine la operacion de medios.');
    data.current.mediaBusy = true;
    setMediaBusy(true);
    try {
      return await enqueue(async () => {
        await saveCurrent();
        const current = data.current;
        let result;
        if (action === 'rename') result = await creatorApi.renameAsset(current.session, asset.id, { revision: current.project.revision, filename: payload.filename });
        else if (action === 'delete') result = await creatorApi.deleteAsset(current.session, asset.id, { revision: current.project.revision, remove_clips: Boolean(payload.remove_clips) });
        else if (action === 'replace') {
          const uploaded = await creatorApi.upload(current.session, payload.file);
          current.project = { ...uploaded.project, ...current.history.present };
            if (alive.current) setProject(current.project);
          result = await creatorApi.replaceAsset(current.session, asset.id, { revision: current.project.revision, replacement_asset_id: uploaded.asset.id });
        } else throw new Error('Accion no disponible para este archivo.');
        const nextHistory = action === 'delete' ? createHistory(result.project) : pushHistory(current.history, result.project, 'Reemplazar medio');
        current.project = result.project;
        current.history = nextHistory;
        current.version += 1;
        current.savedVersion = current.version;
        if (alive.current) { setHistory(nextHistory); setProject(result.project); setEditVersion(current.version); setSaveState('saved'); setExportJob(null); }
        return result;
      });
    } catch (failure) { throw new Error(creatorError(failure, failure.message || 'No se pudo administrar el medio.')); }
    finally { data.current.mediaBusy = false; if (alive.current) setMediaBusy(false); }
  }, [enqueue, saveCurrent]);

  const exportIsActive = activeExport(exportJob);
  const exportJobId = exportJob?.job_id;
  useEffect(() => {
    if (!session || !exportIsActive) return undefined;
    const controller = new AbortController();
    let timer;
    const poll = async () => {
      try {
        const result = await creatorApi.exportStatus(session, controller.signal);
        if (controller.signal.aborted) return;
        setExportJob(result);
        if (['failed', 'error'].includes(result.state)) setError(result.message || result.error || 'La exportacion fallo. El proyecto sigue disponible.');
        if (activeExport(result)) timer = setTimeout(poll, 1200);
      } catch (failure) {
        if (controller.signal.aborted) return;
        setError(creatorError(failure, 'No se pudo consultar el render. Reintentando...'));
        timer = setTimeout(poll, 3000);
      }
    };
    timer = setTimeout(poll, 500);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [session, exportJobId, exportIsActive]);

  const startExport = async settings => {
    setExportBusy(true); setExportJob(null); setError('');
    try {
      await enqueue(async () => {
        await saveCurrent();
        // Edits during a save need their own revision before capturing the render.
        while (data.current.version !== data.current.savedVersion) await saveCurrent();
        const result = await creatorApi.export(data.current.session, { ...settings, revision: data.current.project.revision });
        if (alive.current) setExportJob(result);
      });
    } catch (failure) { if (alive.current) setError(creatorError(failure, 'No se pudo iniciar la exportacion. Revisa el guardado y reintenta.')); }
    finally { if (alive.current) setExportBusy(false); }
  };
  const cancelExport = async () => {
    try {
      const result = await creatorApi.cancelExport(data.current.session);
      if (alive.current) setExportJob(result?.state ? result : { ...exportJob, state: 'cancelling', stage: 'Cancelando...' });
    } catch (failure) { if (alive.current) setError(creatorError(failure, 'No se pudo cancelar. Reintenta.')); }
  };
  const sync = async (camera, master, signal) => {
    try { return await creatorApi.sync(data.current.session, camera, master, signal); }
    catch (failure) { throw new Error(creatorError(failure, 'No se pudo sincronizar. Revisa que ambos archivos contengan audio.')); }
  };
  return <main className="creator-page ph-no-capture" onPointerDown={activity} onKeyDown={activity}>
    {loading && <div className="creator-opening" role="status" data-testid="creator-loading"><AudioWaveform /><h1>STEELMORA CREATOR STUDIO</h1><p>Crea videos, reels y contenido musical desde tu navegador.</p><span>Abriendo proyecto...</span></div>}
    {error && <div className="creator-page-error" role="alert" data-testid="creator-page-error"><span>{error}</span>
      {!project && <button type="button" className="creator-secondary" onClick={() => initialize(recoveryExpired)}><RotateCw />{recoveryExpired ? 'Crear nuevo proyecto' : 'Reintentar'}</button>}
      {project && saveState === 'error' && <button type="button" className="creator-secondary" onClick={save}>Reintentar guardado</button>}
      {project && !capabilities && <button type="button" className="creator-secondary" onClick={() => creatorApi.capabilities().then(result => { setCapabilities(result); setError(''); }).catch(() => {})}>Consultar formatos</button>}
      {project && failedUpload && <button type="button" className="creator-secondary" disabled={uploading} onClick={() => upload(failedUpload.file, failedUpload.options).catch(() => {})}>Reintentar carga</button>}
      {project && saveState !== 'error' && capabilities && <button type="button" className="creator-secondary" onClick={() => setError('')}>Cerrar</button>}
    </div>}
    {storageWarning && <p role="status" className="creator-storage-warning">El navegador no permite guardar la sesion. El proyecto solo se podra recuperar mientras esta pestana siga abierta.</p>}
    {!loading && project && <CreatorEditor {...{ project, history, session, capabilities, uploading, uploadProgress, uploadStage, exportJob, exportBusy, saveState, mediaBusy }} onMediaAction={mediaAction}
      onCancelUpload={() => uploadController.current?.abort()}
      onChange={change} onUndo={() => commitHistory(undoHistory(data.current.history))} onRedo={() => commitHistory(redoHistory(data.current.history))}
      canUndo={Boolean(history?.past.length)} canRedo={Boolean(history?.future.length)} onSave={save} onFile={upload} onSync={sync} onExport={startExport} onCancelExport={cancelExport} onPlayingChange={setPlaying} />}
  </main>;
}
